// F-148 Reviewed Stock Receiving — React Query hooks (design §2.3).
// - Save: one request in flight; the latest request waits in the queue. The save token is a UUID
//   per attempt and is reused only for retries of that same payload.
// - Search: infinite, 250 ms debounce, each new search aborts the previous one.
// - Post and correct: retry `retryable` errors 3× at 300/900/2000 ms ±20%, with the same key.
// - Post success invalidates the `receiving`, `stock` and `barcode` roots; correct does so on
//   settle (success or failure) so a lost response never leaves the receipt stale.

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { SupabaseClient } from '../supabase/client';
import {
  correctReceiptLine,
  createReceivingDraft,
  discardReceivingDraft,
  getReceivingAccess,
  getReceivingDraft,
  getStockReceipt,
  importReceivingRows,
  listReceiving,
  listReceivingSuppliers,
  postReceivingDraft,
  previewReceivingPost,
  saveReceivingDraft,
  searchReceivingVariants,
  toStockReceivingError,
  type StockReceivingError,
} from '../services/stock-receiving.service';
import type {
  CorrectReceiptLineInput,
  CorrectReceiptLineResult,
  CreateReceivingDraftInput,
  DiscardReceivingDraftInput,
  ImportReceivingRowsInput,
  ImportReceivingRowsResult,
  Page,
  PostReceivingInput,
  PostReceivingResult,
  ReceivingAccess,
  ReceivingDraft,
  ReceivingListItem,
  ReceivingPreview,
  ReceivingSupplier,
  SaveReceivingDraftInput,
  SaveReceivingDraftResult,
  SearchReceivingVariantsResult,
  StockReceipt,
} from '../types/stock-receiving';

export const receivingKeys = {
  all: ['receiving'] as const,
  access: (storeId: string) => ['receiving', 'access', storeId] as const,
  suppliers: (storeId: string, search: string) =>
    ['receiving', 'suppliers', storeId, search] as const,
  draft: (draftId: string) => ['receiving', 'draft', draftId] as const,
  preview: (draftId: string, version: number) =>
    ['receiving', 'preview', draftId, version] as const,
  list: (storeId: string, kind: 'draft' | 'receipt') =>
    ['receiving', 'list', storeId, kind] as const,
  receipt: (receiptId: string) => ['receiving', 'receipt', receiptId] as const,
  variantSearch: (storeId: string, query: string) =>
    ['receiving', 'variant-search', storeId, query] as const,
};

export const RECEIVING_RETRY_DELAYS_MS = [300, 900, 2000] as const;
export const RECEIVING_RETRY_JITTER = 0.2;
export const RECEIVING_SEARCH_DEBOUNCE_MS = 250;

/** Delay for retry attempt `attempt` (0-based) with ±20% jitter; `random` ∈ [0, 1). */
export function computeReceivingRetryDelayMs(
  attempt: number,
  random: number = Math.random(),
): number {
  const base =
    RECEIVING_RETRY_DELAYS_MS[Math.min(attempt, RECEIVING_RETRY_DELAYS_MS.length - 1)] ?? 2000;
  const factor = 1 - RECEIVING_RETRY_JITTER + random * 2 * RECEIVING_RETRY_JITTER;
  return Math.round(base * factor);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Runs `operation` and retries only `retryable` StockReceivingErrors, at most
 * `RECEIVING_RETRY_DELAYS_MS.length` times, with the SAME input (same idempotency key).
 */
export async function runWithReceivingRetry<T>(
  operation: () => Promise<T>,
  options?: { delay?: (attempt: number) => number; wait?: (ms: number) => Promise<void> },
): Promise<T> {
  const delay = options?.delay ?? computeReceivingRetryDelayMs;
  const wait = options?.wait ?? sleep;
  let attempt = 0;
  for (;;) {
    try {
      return await operation();
    } catch (err) {
      const mapped = toStockReceivingError(err);
      if (!mapped.retryable || attempt >= RECEIVING_RETRY_DELAYS_MS.length) throw mapped;
      await wait(delay(attempt));
      attempt += 1;
    }
  }
}

export async function invalidateReceivingRelatedQueries(
  queryClient: QueryClient,
  args: { storeId?: string | null; draftId?: string | null; receiptId?: string | null } = {},
): Promise<void> {
  const invalidations = [
    queryClient.invalidateQueries({ queryKey: receivingKeys.all }),
    queryClient.invalidateQueries({ queryKey: ['stock'] }),
    queryClient.invalidateQueries({
      queryKey: args.storeId ? ['stock-summary', args.storeId] : ['stock-summary'],
    }),
    queryClient.invalidateQueries({ queryKey: ['barcode'] }),
  ];
  await Promise.all(invalidations);
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useReceivingAccess(
  client: SupabaseClient,
  storeId: string | null,
): UseQueryResult<ReceivingAccess, StockReceivingError> {
  return useQuery<ReceivingAccess, StockReceivingError>({
    queryKey: receivingKeys.access(storeId ?? 'no-store'),
    enabled: Boolean(storeId),
    staleTime: 30_000,
    queryFn: () => {
      if (!storeId) return Promise.resolve({ enabled: false, canManage: false });
      return getReceivingAccess(client, storeId);
    },
  });
}

export function useReceivingSuppliers(
  client: SupabaseClient,
  storeId: string | null,
  search: string,
): UseQueryResult<ReceivingSupplier[], StockReceivingError> {
  const debounced = useDebouncedValue(search.trim(), RECEIVING_SEARCH_DEBOUNCE_MS);
  return useQuery<ReceivingSupplier[], StockReceivingError>({
    queryKey: receivingKeys.suppliers(storeId ?? 'no-store', debounced),
    enabled: Boolean(storeId),
    staleTime: 30_000,
    queryFn: () => {
      if (!storeId) return Promise.resolve([]);
      return listReceivingSuppliers(client, storeId, debounced);
    },
  });
}

export function useReceivingDraft(
  client: SupabaseClient,
  draftId: string | null,
): UseQueryResult<ReceivingDraft, StockReceivingError> {
  return useQuery<ReceivingDraft, StockReceivingError>({
    queryKey: receivingKeys.draft(draftId ?? 'no-draft'),
    enabled: Boolean(draftId),
    queryFn: () => {
      if (!draftId)
        return Promise.reject(toStockReceivingError({ code: 'P0480', message: 'F148_NOT_FOUND' }));
      return getReceivingDraft(client, draftId);
    },
  });
}

export function useReceivingPreview(
  client: SupabaseClient,
  draftId: string | null,
  version: number | null,
): UseQueryResult<ReceivingPreview, StockReceivingError> {
  return useQuery<ReceivingPreview, StockReceivingError>({
    queryKey: receivingKeys.preview(draftId ?? 'no-draft', version ?? 0),
    enabled: Boolean(draftId) && version !== null,
    staleTime: 0,
    queryFn: () => {
      if (!draftId)
        return Promise.reject(toStockReceivingError({ code: 'P0480', message: 'F148_NOT_FOUND' }));
      return previewReceivingPost(client, draftId);
    },
  });
}

export function useStockReceipt(
  client: SupabaseClient,
  receiptId: string | null,
): UseQueryResult<StockReceipt, StockReceivingError> {
  return useQuery<StockReceipt, StockReceivingError>({
    queryKey: receivingKeys.receipt(receiptId ?? 'no-receipt'),
    enabled: Boolean(receiptId),
    queryFn: () => {
      if (!receiptId)
        return Promise.reject(toStockReceivingError({ code: 'P0480', message: 'F148_NOT_FOUND' }));
      return getStockReceipt(client, receiptId);
    },
  });
}

export function useReceivingList(
  client: SupabaseClient,
  storeId: string | null,
  kind: 'draft' | 'receipt',
): UseInfiniteQueryResult<
  InfiniteData<Page<ReceivingListItem>, string | null>,
  StockReceivingError
> {
  return useInfiniteQuery<
    Page<ReceivingListItem>,
    StockReceivingError,
    InfiniteData<Page<ReceivingListItem>, string | null>,
    ReturnType<typeof receivingKeys.list>,
    string | null
  >({
    queryKey: receivingKeys.list(storeId ?? 'no-store', kind),
    enabled: Boolean(storeId),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    queryFn: ({ pageParam }) => {
      if (!storeId) return Promise.resolve({ items: [], nextCursor: null });
      return listReceiving(client, { storeId, kind, cursor: pageParam });
    },
  });
}

/** Debounces a value; the debounced value updates `delayMs` after the last change. */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

function linkAbortSignals(primary: AbortSignal | undefined, secondary: AbortSignal): AbortSignal {
  if (!primary) return secondary;
  const controller = new AbortController();
  const abort = (): void => controller.abort();
  if (primary.aborted || secondary.aborted) {
    controller.abort();
  } else {
    primary.addEventListener('abort', abort, { once: true });
    secondary.addEventListener('abort', abort, { once: true });
  }
  return controller.signal;
}

export function useReceivingVariantSearch(
  client: SupabaseClient,
  storeId: string | null,
  query: string,
): UseInfiniteQueryResult<
  InfiniteData<SearchReceivingVariantsResult, string | null>,
  StockReceivingError
> {
  const debounced = useDebouncedValue(query.trim(), RECEIVING_SEARCH_DEBOUNCE_MS);
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    controllerRef.current?.abort();
    controllerRef.current = new AbortController();
    return () => {
      controllerRef.current?.abort();
    };
  }, [debounced, storeId]);

  return useInfiniteQuery<
    SearchReceivingVariantsResult,
    StockReceivingError,
    InfiniteData<SearchReceivingVariantsResult, string | null>,
    ReturnType<typeof receivingKeys.variantSearch>,
    string | null
  >({
    queryKey: receivingKeys.variantSearch(storeId ?? 'no-store', debounced),
    enabled: Boolean(storeId),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    queryFn: ({ pageParam, signal }) => {
      if (!storeId) return Promise.resolve({ items: [], nextCursor: null, exactBarcode: null });
      const own = (controllerRef.current ??= new AbortController()).signal;
      return searchReceivingVariants(
        client,
        { storeId, query: debounced, cursor: pageParam },
        { signal: linkAbortSignals(signal, own) },
      );
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateReceivingDraft(
  client: SupabaseClient,
): UseMutationResult<ReceivingDraft, StockReceivingError, CreateReceivingDraftInput> {
  const queryClient = useQueryClient();
  return useMutation<ReceivingDraft, StockReceivingError, CreateReceivingDraftInput>({
    retry: false,
    mutationKey: ['receiving', 'create-draft'] as const,
    mutationFn: (input) => createReceivingDraft(client, input),
    onSuccess: async (draft) => {
      queryClient.setQueryData(receivingKeys.draft(draft.id), draft);
      await queryClient.invalidateQueries({ queryKey: receivingKeys.list(draft.storeId, 'draft') });
    },
  });
}

interface Deferred<T> {
  resolve: (value: T) => void;
  reject: (reason: StockReceivingError) => void;
}

interface QueuedSave {
  input: SaveReceivingDraftInput;
  deferreds: Deferred<SaveReceivingDraftResult>[];
}

export interface UseSaveReceivingDraftResult {
  /** Enqueues a save. Only the latest waiting request survives; superseded callers get its result. */
  save: (input: SaveReceivingDraftInput) => Promise<SaveReceivingDraftResult>;
  isSaving: boolean;
  lastError: StockReceivingError | null;
  lastResult: SaveReceivingDraftResult | null;
}

export function useSaveReceivingDraft(client: SupabaseClient): UseSaveReceivingDraftResult {
  const queryClient = useQueryClient();
  const inFlightRef = useRef(false);
  const queuedRef = useRef<QueuedSave | null>(null);
  const mountedRef = useRef(true);
  const [isSaving, setIsSaving] = useState(false);
  const [lastError, setLastError] = useState<StockReceivingError | null>(null);
  const [lastResult, setLastResult] = useState<SaveReceivingDraftResult | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const drain = useCallback(async (): Promise<void> => {
    if (inFlightRef.current) return;
    const next = queuedRef.current;
    if (!next) {
      if (mountedRef.current) setIsSaving(false);
      return;
    }
    queuedRef.current = null;
    inFlightRef.current = true;
    if (mountedRef.current) setIsSaving(true);
    try {
      const result = await saveReceivingDraft(client, next.input);
      if (mountedRef.current) {
        setLastResult(result);
        setLastError(null);
      }
      await queryClient.invalidateQueries({ queryKey: receivingKeys.draft(next.input.draftId) });
      next.deferreds.forEach((d) => d.resolve(result));
    } catch (err) {
      const mapped = toStockReceivingError(err);
      if (mountedRef.current) setLastError(mapped);
      next.deferreds.forEach((d) => d.reject(mapped));
    } finally {
      inFlightRef.current = false;
    }
    await drain();
  }, [client, queryClient]);

  const save = useCallback(
    (input: SaveReceivingDraftInput): Promise<SaveReceivingDraftResult> => {
      return new Promise<SaveReceivingDraftResult>((resolve, reject) => {
        const deferred: Deferred<SaveReceivingDraftResult> = { resolve, reject };
        if (queuedRef.current) {
          queuedRef.current = { input, deferreds: [...queuedRef.current.deferreds, deferred] };
        } else {
          queuedRef.current = { input, deferreds: [deferred] };
        }
        void drain();
      });
    },
    [drain],
  );

  return { save, isSaving, lastError, lastResult };
}

export function useImportReceivingRows(
  client: SupabaseClient,
): UseMutationResult<ImportReceivingRowsResult, StockReceivingError, ImportReceivingRowsInput> {
  const queryClient = useQueryClient();
  return useMutation<ImportReceivingRowsResult, StockReceivingError, ImportReceivingRowsInput>({
    retry: false,
    mutationKey: ['receiving', 'import-rows'] as const,
    mutationFn: (input) => importReceivingRows(client, input),
    onSuccess: async (result, input) => {
      if (result.outcome === 'imported') {
        await queryClient.invalidateQueries({ queryKey: receivingKeys.draft(input.draftId) });
      }
    },
  });
}

export function useDiscardReceivingDraft(
  client: SupabaseClient,
): UseMutationResult<void, StockReceivingError, DiscardReceivingDraftInput> {
  const queryClient = useQueryClient();
  return useMutation<void, StockReceivingError, DiscardReceivingDraftInput>({
    retry: false,
    mutationKey: ['receiving', 'discard-draft'] as const,
    mutationFn: (input) => discardReceivingDraft(client, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: receivingKeys.all });
    },
  });
}

export function usePostReceivingDraft(
  client: SupabaseClient,
): UseMutationResult<PostReceivingResult, StockReceivingError, PostReceivingInput> {
  const queryClient = useQueryClient();
  return useMutation<PostReceivingResult, StockReceivingError, PostReceivingInput>({
    retry: false,
    mutationKey: ['receiving', 'post-draft'] as const,
    mutationFn: (input) => runWithReceivingRetry(() => postReceivingDraft(client, input)),
    onSuccess: async (_result, input) => {
      await invalidateReceivingRelatedQueries(queryClient, { draftId: input.draftId });
    },
  });
}

/**
 * Correction mutation. Invalidation runs `onSettled` (success AND failure) and is awaited, so
 * `isPending` stays true until the receipt/stock/list refetches have been issued. After an
 * ambiguous failure (transport: the server may have committed the correction while the response
 * was lost) the reopened dialog therefore sees the committed correction and reduced
 * `correctableUnits` instead of stale data. The key-per-dialog-open contract is unchanged; the
 * bounded same-key retry stays inside `runWithReceivingRetry`.
 */
export function useCorrectReceiptLine(
  client: SupabaseClient,
): UseMutationResult<CorrectReceiptLineResult, StockReceivingError, CorrectReceiptLineInput> {
  const queryClient = useQueryClient();
  return useMutation<CorrectReceiptLineResult, StockReceivingError, CorrectReceiptLineInput>({
    retry: false,
    mutationKey: ['receiving', 'correct-line'] as const,
    mutationFn: (input) => runWithReceivingRetry(() => correctReceiptLine(client, input)),
    onSettled: async () => {
      try {
        await invalidateReceivingRelatedQueries(queryClient);
      } catch {
        // Query state exposes refresh failure. Preserve the real mutation outcome without
        // logging an unclassified error (the immutable response or StockReceivingError).
      }
    },
  });
}
